// src/pages/AddLeadPage.js
import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  ListItemText,
  Typography,
  Tabs,
  Tab,
  TextField,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Divider,
  Modal,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import UnifiedPageWrapper from "../components/Layout/UnifiedPageWrapper";
import { useNavigate, useSearchParams } from "react-router-dom";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import API from "../utils/api";
import { toast } from "react-toastify";
import { emitLeadUpdate, subscribeLeadUpdates } from "../utils/leadSync";
import useAuth from "../hooks/useAuth";
import ConfirmDialog from "../components/ConfirmDialog";
import { LeadStatusSelectMenuItems } from "../components/LeadStatusMenuItems";
import StateSelect from "../components/StateSelect";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";



export default function AddLeadPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [searchParams] = useSearchParams();
  const territoryId = searchParams.get("territory_id");
  const pageView = searchParams.get("view") || "all";
  const showEditTerritory = pageView === "edit" || pageView === "all";
  const showUploadLeads = pageView === "upload" || pageView === "all";

  const [tab, setTab] = useState(0);
  const [territory, setTerritory] = useState(null);
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [file, setFile] = useState(null);
  const [selectedReps, setSelectedReps] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  
  // Import summary state
  const [importSummary, setImportSummary] = useState(null);
  const [showSkippedDetails, setShowSkippedDetails] = useState(false);
 

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    status: "New",
    assigned_rep: "",
    latitude: "",
    longitude: "",
    territory: territoryId || "",
    notes: "",
    additional_notes: "",
    sold_date: "",
    property_type: "",
    price: "",
    square_feet: "",
    year_built: "",
  });

  const { can, isReadOnly, role } = useAuth();
  const canEditTerritoryName = role === "Admin" && !isReadOnly;
  const [confirmDeleteTerritory, setConfirmDeleteTerritory] = useState(false);

  // helper to normalize manager to an id
  const getManagerId = (mgr) =>
    mgr && typeof mgr === "object" ? mgr.id : mgr || null;

  const fetchData = async () => {
    try {
      if (!territoryId) return;
      const [territoryRes, leadsRes, usersRes] = await Promise.all([
        API.get(`/territories/${territoryId}/`),
        API.get(`/leads/?territory=${territoryId}`),
        API.get("/users/"),
      ]);
      // normalize manager to an id for stable Select value
      const t = territoryRes.data || null;
      const normalized = t
        ? { ...t, manager: getManagerId(t.manager) }
        : null;

      setTerritory(normalized);
      setLeads(leadsRes.data);
      setUsers(usersRes.data);

      // Pre-populate selected reps from territory
    if (normalized?.assigned_reps) {
      setSelectedReps(normalized.assigned_reps.map(r => r.id));
    }
    } catch (err) {
      console.error("Error fetching add-lead data:", err);
      toast.error("Failed to load territory or leads");
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [territoryId]);

  const handleFileUpload = async () => {
    if (!file) return toast.error("Please select a file first.");
    
    // Ensure territory ID is always passed when available from URL
    if (!territoryId) {
      toast.error("Territory ID is missing. Please navigate from a territory page.");
      return;
    }
    
    const data = new FormData();
    data.append("file", file);
    data.append("territory", territoryId); // Always append when territoryId exists

    setIsUploading(true); // Start loading

    try {
      const response = await API.post("/import-leads/", data, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000, // 5 minutes for large file uploads (processing + geocoding can take time)
      });

      const result = response.data;
      
      if (result.error) {
        toast.error(`❌ Upload failed: ${result.error}`);
        setIsUploading(false);
        return;
      }

      const hasCreated = (result.created ?? 0) > 0;
      const hasEnriched = (result.enriched ?? 0) > 0;
      const hasConflicted = (result.conflicted ?? 0) > 0;
      const hasSkipped = (result.skipped ?? 0) > 0;

      const partialNote = result.partial
        ? ` Stopped early (${result.stopped_reason || "time_budget"}): ${result.rows_remaining ?? 0} data rows not processed — re-upload to continue (dedupe applies).`
        : "";

      if (hasCreated || hasEnriched) {
        const parts = [];
        if (hasCreated) parts.push(`${result.created} created`);
        if (hasEnriched) parts.push(`${result.enriched} enriched`);
        if (hasConflicted) parts.push(`${result.conflicted} conflicted`);
        if (hasSkipped) parts.push(`${result.skipped} skipped`);
        toast.success(`✅ Import complete: ${parts.join(", ")}.${partialNote}`);
        setFile(null);
        await refreshLeads();

        const updatedLeadsRes = await API.get(territoryId ? `/leads/?territory=${territoryId}` : '/leads/');
        const updatedLeadsCount = updatedLeadsRes.data?.length || 0;

        setImportSummary({
          uploaded: result.created ?? 0,
          enriched: result.enriched ?? 0,
          conflicted: result.conflicted ?? 0,
          unchanged: result.unchanged ?? 0,
          skipped: result.skipped ?? 0,
          totalInTerritory: updatedLeadsCount,
          errors: result.errors || [],
        });
        setShowSkippedDetails(false);
      } else if (hasSkipped || hasConflicted) {
        const parts = [];
        if (hasConflicted) parts.push(`${result.conflicted} conflicted`);
        if (hasSkipped) parts.push(`${result.skipped} skipped`);
        toast.warning(`⚠️ No new leads created. ${parts.join(", ")}.${partialNote}`);
        if (result.errors && result.errors.length > 0) {
          console.error("Upload errors:", result.errors);
        }
        const currentLeadsRes = await API.get(territoryId ? `/leads/?territory=${territoryId}` : '/leads/');
        const currentLeadsCount = currentLeadsRes.data?.length || 0;

        setImportSummary({
          uploaded: 0,
          enriched: result.enriched ?? 0,
          conflicted: result.conflicted ?? 0,
          unchanged: result.unchanged ?? 0,
          skipped: result.skipped ?? 0,
          totalInTerritory: currentLeadsCount,
          errors: result.errors || [],
        });
        setShowSkippedDetails(false);
      } else if (result.partial) {
        toast.warning(
          `Import stopped early (${result.stopped_reason || "time_budget"}). ${result.rows_processed ?? 0} of ${result.total_rows ?? "?"} data rows processed; ${result.rows_remaining ?? 0} not processed — re-upload to continue (dedupe applies).`
        );
        setFile(null);
        await refreshLeads();
        const currentLeadsRes = await API.get(territoryId ? `/leads/?territory=${territoryId}` : "/leads/");
        const currentLeadsCount = currentLeadsRes.data?.length || 0;
        setImportSummary({
          uploaded: result.created ?? 0,
          enriched: result.enriched ?? 0,
          conflicted: result.conflicted ?? 0,
          unchanged: result.unchanged ?? 0,
          skipped: result.skipped ?? 0,
          totalInTerritory: currentLeadsCount,
          errors: result.errors || [],
        });
        setShowSkippedDetails(false);
      } else {
        toast.warning("⚠️ No leads were created. Please check your file format.");
        setImportSummary(null);
      }
    } catch (err) {
      console.error("Upload error:", err);
      const errorMsg = err.response?.data?.error || err.response?.data?.detail || err.message || "Upload failed";
      toast.error(`❌ ${errorMsg}`);
    } finally {
      setIsUploading(false); // Always stop loading
    }
  };

  const handleSaveLead = async () => {
    const hasCoords = formData.latitude && formData.longitude;
    const hasAddress = formData.address && formData.city && formData.state;
    if (!hasCoords && !hasAddress) {
      toast.warning("⚠️ Please provide either Latitude & Longitude or a full address (Street, City, State).");
      return;
    }

    try {
      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zip_code,
        status: formData.status,
        assigned_rep: formData.assigned_rep || null,
        latitude: hasCoords ? parseFloat(formData.latitude) : null,
        longitude: hasCoords ? parseFloat(formData.longitude) : null,
        territory: territoryId || formData.territory || null,
	      notes: formData.notes || "",
        sold_date: formData.sold_date || null,
	property_type: formData.property_type || "",
	price: formData.price || null,
	square_feet: formData.square_feet || null,
	year_built: formData.year_built || null,
      };

      const response = await API.post("/leads/", payload);
      toast.success("Lead added successfully!");
      emitLeadUpdate(response.data);
      await refreshLeads();
      setFormData({
        first_name: "",
        last_name: "",
        address: "",
        city: "",
        state: "",
        zip_code: "",
        status: "New",
        assigned_rep: "",
        latitude: "",
        longitude: "",
        territory: territoryId || "",
        notes: "",
        sold_date: "",
        property_type: "",
        price: "",
        square_feet: "",
        year_built: "",
      });
    } catch (err) {
      console.error("Error saving lead:", err);
      toast.error("Failed to add lead");
    }
  };

  const refreshLeads = useCallback(async () => {
    try {
      // Build URL properly - only add territory parameter if it exists
      const url = territoryId 
        ? `/leads/?territory=${territoryId}`
        : '/leads/';
      const res = await API.get(url);
      setLeads(res.data);
    } catch (err) {
      console.error("Error refreshing leads:", err);
    }
  }, [territoryId]);

  useEffect(() => {
    const unsubscribe = subscribeLeadUpdates(() => {
      setTimeout(() => refreshLeads(), 300);
    });
    return () => unsubscribe();
  }, [refreshLeads]);

  const handleCancel = () => navigate("/territories");

  // ============= CONTAINER 1 handlers (fixed) =============
  const handleManagerChange = (e) => {
    const managerId = e.target.value || null;
    setTerritory((prev) => ({ ...prev, manager: managerId }));
  };

  const handleTerritoryNameChange = (e) => {
    const v = e.target.value;
    setTerritory((prev) => (prev ? { ...prev, name: v } : prev));
  };

  const handleSaveTerritory = async () => {
    try {
      if (!territory) return;
      await API.put(`/territories/${territory.id}/`, {
        name: territory.name,
        // IMPORTANT: serializer expects `manager`, not `manager_id`
        manager: getManagerId(territory.manager),
        assigned_reps_ids: selectedReps,
      });
      await API.post(`/territories/${territory.id}/sync_leads/`);
      await fetchData();
      await refreshLeads();
  

      toast.success("Territory updated successfully!");
      // refresh local view so the selected manager shows immediately
      await fetchData();
      setSelectedReps([]);
    } catch (err) {
      console.error("Error updating territory:", err);
      toast.error("Failed to update territory");
    }
  };

  const handleDeleteTerritory = () => {
    if (!territory?.id) return;
    setConfirmDeleteTerritory(true);
  };

  const handleConfirmDeleteTerritory = async () => {
    try {
      await API.delete(`/territories/${territory.id}/`);
      toast.success("Territory deleted successfully");
      navigate("/territories");
    } catch (err) {
      console.error("Error deleting territory:", err);
      toast.error("Failed to delete territory");
    } finally {
      setConfirmDeleteTerritory(false);
    }
  };

  // ============= Container 2 handlers (restored) =============
  // Handle view leads navigation
  const handleViewLeads = () => {
    if (territoryId) {
      navigate(`/leads/search?territory=${territoryId}`);
    } else {
      navigate("/leads/search");
    }
  };

  const timestamp = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}-${String(d.getHours()).padStart(2, "0")}${String(d.getMinutes()).padStart(2, "0")}`;
  };

  const handleDownloadSkippedCSV = () => {
    if (!importSummary?.errors?.length) return;
    const header = "Row,Address,Reason\n";
    const escapeCsv = (v) => {
      const s = String(v ?? "").replace(/"/g, '""');
      return s.includes(",") || s.includes("\n") || s.includes('"') ? `"${s}"` : s;
    };
    const rows = importSummary.errors.map(
      (e) => `${e.row},${escapeCsv(e.address ?? "—")},${escapeCsv(e.reason)}`
    );
    const blob = new Blob(["\uFEFF" + header + rows.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `lead-import-skipped-${timestamp()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadSkippedTXT = () => {
    if (!importSummary?.errors?.length) return;
    const lines = ["Row\tAddress\tReason", ...importSummary.errors.map((e) => `${e.row}\t${(e.address ?? "—").replace(/\t/g, " ")}\t${(e.reason ?? "").replace(/\t/g, " ")}`)];
    const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `lead-import-skipped-${timestamp()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        p: { xs: 1.5, sm: 2, md: 3 },
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 1500, mx: "auto" }}>
        {/* Breadcrumb */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          flexWrap="wrap"
          sx={{ mb: 1.5, px: { xs: 0.5, md: 0 } }}
        >
          <Button
            onClick={() => navigate("/territories")}
            size="small"
            sx={{
              minWidth: 0,
              p: 0,
              textTransform: "none",
              color: "text.secondary",
              fontWeight: 500,
              "&:hover": { backgroundColor: "transparent", color: "primary.main" },
            }}
          >
            Territories
          </Button>
          {territory?.name && (
            <>
              <Typography variant="body2" color="text.disabled">›</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: "primary.main" }}>
                {territory.name}
              </Typography>
            </>
          )}
          <Typography variant="body2" color="text.disabled">›</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {pageView === "upload" ? "Upload Leads" : "Edit Territory"}
          </Typography>
        </Stack>

        {/* ====================== TERRITORY EDIT CARD ====================== */}
        {showEditTerritory && (
        <Paper
          data-tour="territory-edit-container"
          elevation={0}
          sx={{
            overflow: "hidden",
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 8px 28px rgba(0,0,0,0.18)"
                : "0 8px 28px rgba(15,23,42,0.06)",
            mb: 2.5,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: { xs: 2, md: 2.5 },
              display: "flex",
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
              background:
                theme.palette.mode === "dark"
                  ? alpha(theme.palette.primary.main, 0.035)
                  : `linear-gradient(110deg, ${alpha(theme.palette.primary.main, 0.035)} 0%, ${alpha(theme.palette.primary.main, 0.012)} 100%)`,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack direction="row" spacing={1.75} alignItems="center">
              <Box
                sx={{
                  width: 54,
                  height: 54,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                  color: "primary.main",
                  backgroundColor: alpha(theme.palette.primary.main, 0.09),
                }}
              >
                <LocationOnOutlinedIcon sx={{ fontSize: 30 }} />
              </Box>

              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    lineHeight: 1.2,
                    fontSize: { xs: "1.2rem", md: "1.35rem" },
                  }}
                >
                  Edit Territory
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.35 }}
                >
                  Update territory details and assign a manager.
                </Typography>
              </Box>
            </Stack>

            <Button
              variant="outlined"
              size="small"
              startIcon={<ArrowBackIcon sx={{ fontSize: "17px !important" }} />}
              onClick={() => navigate("/territories")}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                px: 1.75,
                whiteSpace: "nowrap",
                fontWeight: 600,
              }}
            >
              Back to Territories
            </Button>
          </Box>

          {/* Fields */}
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                columnGap: 4,
                rowGap: 2.25,
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>
                  Territory Name
                  {canEditTerritoryName && (
                    <Box component="span" sx={{ color: "error.main" }}> *</Box>
                  )}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={territory?.name || ""}
                  disabled={!canEditTerritoryName}
                  onChange={handleTerritoryNameChange}
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: 2 },
                    "& .MuiInputBase-input": { fontSize: "0.875rem" },
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.55 }}
                >
                  {canEditTerritoryName
                    ? "Admin only: the new name appears everywhere this territory is shown."
                    : "Only an Admin can change the territory name."}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>
                  Manager
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={territory ? getManagerId(territory.manager) ?? "" : ""}
                    onChange={handleManagerChange}
                    displayEmpty
                    sx={{
                      borderRadius: 2,
                      fontSize: "0.875rem",
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          mt: 0.5,
                          borderRadius: 2.5,
                          border: "1px solid",
                          borderColor: "divider",
                          boxShadow:
                            theme.palette.mode === "dark"
                              ? "0 10px 28px rgba(0,0,0,0.30)"
                              : "0 10px 28px rgba(15,23,42,0.09)",
                          p: 0.5,
                          "& .MuiMenuItem-root": {
                            fontSize: "0.8125rem",
                            minHeight: 36,
                            borderRadius: 1.5,
                          },
                        },
                      },
                    }}
                    renderValue={(selected) => {
                      if (!selected) return <Typography color="text.secondary" fontSize="0.875rem">None</Typography>;
                      const user = users.find((u) => u.id === selected);
                      if (!user) return selected;
                      const initials = `${user.first_name?.[0] || ""}${user.last_name?.[0] || ""}`.toUpperCase();
                      return (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box
                            sx={{
                              width: 25,
                              height: 25,
                              borderRadius: "50%",
                              display: "grid",
                              placeItems: "center",
                              bgcolor: alpha(theme.palette.primary.main, 0.12),
                              color: "primary.main",
                              fontSize: "0.68rem",
                              fontWeight: 700,
                            }}
                          >
                            {initials}
                          </Box>
                          <span>{user.first_name} {user.last_name}</span>
                        </Stack>
                      );
                    }}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {users.map((u) => (
                      <MenuItem key={u.id} value={u.id}>
                        {u.first_name} {u.last_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>
                  Lead Count
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={leads.length}
                  disabled
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: 2 },
                    "& .MuiInputBase-input": { fontSize: "0.875rem" },
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.55 }}
                >
                  Number of leads currently assigned to this territory.
                </Typography>
              </Box>

              <Box
                sx={{
                  alignSelf: "start",
                  mt: { xs: 0, md: 0.25 },
                  p: 1.5,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: alpha(theme.palette.primary.main, 0.25),
                  backgroundColor: alpha(theme.palette.primary.main, 0.045),
                  display: "flex",
                  gap: 1.25,
                  alignItems: "flex-start",
                }}
              >
                <InfoOutlinedIcon color="primary" sx={{ fontSize: 22, mt: 0.1 }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Territory Information
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Changing the manager will update access and visibility for this territory.
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ my: 2.25 }} />

            <Stack
              direction={{ xs: "column-reverse", sm: "row" }}
              spacing={1.25}
              justifyContent="flex-end"
            >
              {can.canDelete && territory?.id && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={handleDeleteTerritory}
                  disabled={isReadOnly}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    px: 2,
                    minWidth: { sm: 150 },
                    fontWeight: 600,
                  }}
                >
                  Delete Territory
                </Button>
              )}

              <Button
                variant="outlined"
                color="inherit"
                onClick={handleCancel}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  px: 2,
                  minWidth: { sm: 120 },
                  fontWeight: 600,
                }}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveTerritory}
                disabled={isReadOnly}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  px: 2.5,
                  minWidth: { sm: 135 },
                  fontWeight: 600,
                  boxShadow: "none",
                }}
              >
                Save Territory
              </Button>
            </Stack>
          </Box>
        </Paper>
        )}

        {/* ====================== BULK UPLOAD / ADD LEAD CARD ====================== */}
        {showUploadLeads && (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 8px 28px rgba(0,0,0,0.18)"
                : "0 8px 28px rgba(15,23,42,0.06)",
            p: { xs: 2, md: 2.5 },
            mb: 4,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.25 }}>
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                color: "primary.main",
                backgroundColor: alpha(theme.palette.primary.main, 0.09),
              }}
            >
              <UploadFileIcon />
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, fontSize: { xs: "1.05rem", md: "1.15rem" } }}
              >
                {territory?.name
                  ? `Bulk Upload Leads to ${territory.name}`
                  : "Bulk Upload Leads"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {territory?.name
                  ? `Upload a CSV or Excel file (.csv, .xlsx, .xls) containing your leads. The system will automatically geocode addresses, validate data, and import leads into ${territory.name}.`
                  : "Upload a CSV or Excel file (.csv, .xlsx, .xls) containing your leads. The system will automatically geocode addresses, validate data, and import leads into the selected territory."}
              </Typography>
            </Box>
          </Stack>

          {tab === 0 && (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "minmax(0, 3fr) auto minmax(250px, 1fr)" },
                  alignItems: "stretch",
                  gap: { xs: 1.5, md: 2 },
                }}
              >
                {/* Upload area */}
                <Box
                  data-tour="bulk-upload-leads-container"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (isReadOnly || isUploading) return;
                    const droppedFile = e.dataTransfer.files?.[0];
                    if (droppedFile) {
                      setFile(droppedFile);
                      setImportSummary(null);
                    }
                  }}
                  sx={{
                    minHeight: 205,
                    borderRadius: 2.5,
                    border: "1.5px dashed",
                    borderColor: alpha(theme.palette.primary.main, 0.5),
                    backgroundColor: alpha(theme.palette.primary.main, 0.025),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    px: 2,
                    py: 2.5,
                  }}
                >
                  <Stack spacing={0.8} alignItems="center">
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
                        color: "primary.main",
                        backgroundColor: alpha(theme.palette.primary.main, 0.09),
                      }}
                    >
                      <UploadFileIcon />
                    </Box>

                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      Drag and drop your file here
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      or choose a file to upload
                    </Typography>

                    <Button
                      data-tour="choose-file-button"
                      variant="contained"
                      component="label"
                      size="small"
                      disabled={isReadOnly || isUploading}
                      startIcon={<UploadFileIcon />}
                      sx={{
                        mt: "6px !important",
                        textTransform: "none",
                        borderRadius: 2,
                        px: 2.25,
                        boxShadow: "none",
                        fontWeight: 600,
                      }}
                    >
                      Choose File
                      <input
                        type="file"
                        accept=".csv, .xlsx, .xls"
                        hidden
                        disabled={isReadOnly || isUploading}
                        onChange={(e) => {
                          if (isReadOnly) return;
                          setFile(e.target.files[0]);
                          setImportSummary(null);
                        }}
                      />
                    </Button>

                    {file && (
                      <Typography
                        variant="caption"
                        sx={{ mt: "4px !important", wordBreak: "break-word", fontWeight: 600 }}
                      >
                        {file.name}
                      </Typography>
                    )}

                    <Typography variant="caption" color="text.secondary">
                      Supported formats: .csv, .xlsx, .xls
                    </Typography>
                  </Stack>
                </Box>

                {/* OR divider */}
                <Box
                  sx={{
                    display: { xs: "none", md: "flex" },
                    width: 36,
                    position: "relative",
                    alignItems: "center",
                    justifyContent: "center",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      bottom: 0,
                      left: "50%",
                      width: "1px",
                      backgroundColor: "divider",
                    },
                  }}
                >
                  <Box
                    sx={{
                      zIndex: 1,
                      width: 34,
                      height: 34,
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                      bgcolor: "background.paper",
                      border: "1px solid",
                      borderColor: "divider",
                      fontSize: "0.7rem",
                      fontWeight: 700,
                    }}
                  >
                    OR
                  </Box>
                </Box>

                {/* Manual lead card */}
                <Box
                  sx={{
                    minHeight: 205,
                    borderRadius: 2.5,
                    border: "1px solid",
                    borderColor: alpha(theme.palette.success.main, 0.18),
                    backgroundColor: alpha(theme.palette.success.main, 0.055),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    p: 2,
                  }}
                >
                  <Stack spacing={0.8} alignItems="center" sx={{ width: "100%" }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
                        color: "success.main",
                        backgroundColor: alpha(theme.palette.success.main, 0.10),
                      }}
                    >
                      <PersonAddIcon />
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "success.dark" }}>
                      Add Individual Lead
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {territory?.name
                        ? `Manually add a single lead to ${territory.name}.`
                        : "Manually add a single lead to this territory."}
                    </Typography>
                    <Button
                      data-tour="add-leads-button"
                      variant="outlined"
                      color="success"
                      size="small"
                      startIcon={<PersonAddIcon />}
                      onClick={() => setTab(1)}
                      sx={{
                        width: "100%",
                        mt: "8px !important",
                        textTransform: "none",
                        borderRadius: 2,
                        fontWeight: 600,
                      }}
                    >
                      Add Lead Manually
                    </Button>
                  </Stack>
                </Box>
              </Box>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="center"
                spacing={1}
                sx={{ mt: 1.5 }}
              >
                <Button
                  data-tour="upload-file-button"
                  variant="contained"
                  color="primary"
                  onClick={handleFileUpload}
                  disabled={isReadOnly || !file || isUploading}
                  startIcon={
                    isUploading ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />
                  }
                  size="small"
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    px: 2.5,
                    boxShadow: "none",
                    fontWeight: 600,
                  }}
                >
                  {isUploading ? "Uploading..." : "Upload File"}
                </Button>
              </Stack>
            </>
          )}

          {/* Import summary - shown only after an upload */}
          {importSummary && (
          <Box
            sx={{
              mt: 2,
              p: { xs: 1.5, md: 1.25 },
              borderRadius: 2.25,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: alpha(theme.palette.primary.main, 0.02),
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "minmax(260px, 1fr) auto auto auto" },
              alignItems: "center",
              gap: 1,
            }}
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  color: "primary.main",
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                }}
              >
                <AssessmentOutlinedIcon sx={{ fontSize: 21 }} />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Import Summary
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  After upload, you'll see a summary of uploaded, skipped, and total leads.
                </Typography>
              </Box>
            </Stack>

         {[
  {
    value: importSummary?.uploaded ?? 0,
    label: "Uploaded",
    color: theme.palette.success.main,
  },
  {
    value: importSummary?.skipped ?? 0,
    label: "Skipped",
    color: theme.palette.warning.main,
  },
  {
    value: importSummary
      ? (importSummary.uploaded || 0) + (importSummary.skipped || 0)
      : 0,
    label: "Total",
    color: theme.palette.primary.main,
  },
].map((item) => (
              <Box
                key={item.label}
                sx={{
                  minWidth: { md: 112 },
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  textAlign: "center",
                  border: "1px solid",
                  borderColor: alpha(item.color, 0.22),
                  backgroundColor: alpha(item.color, 0.06),
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 700, color: item.color, lineHeight: 1.1 }}>
                  {item.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Box>

          )}

          {/* Existing detailed import results remain available after an upload */}
          {importSummary && (
            <Box
              sx={{
                mt: 2,
                border: "1px solid",
                borderColor: alpha(theme.palette.success.main, 0.20),
                borderRadius: 2.25,
                p: { xs: 1.5, md: 2 },
                backgroundColor: alpha(theme.palette.success.main, 0.035),
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
                Import Details
              </Typography>

              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>Leads Created</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: "success.main" }}>
                        {importSummary.uploaded ?? 0}
                      </TableCell>
                    </TableRow>
                    {(importSummary.enriched ?? 0) > 0 && (
                      <TableRow>
                        <TableCell>Enriched</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: "info.main" }}>
                          {importSummary.enriched}
                        </TableCell>
                      </TableRow>
                    )}
                    {(importSummary.conflicted ?? 0) > 0 && (
                      <TableRow>
                        <TableCell>Conflicted</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: "warning.main" }}>
                          {importSummary.conflicted}
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell>Skipped</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {importSummary.skipped ?? 0}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Total Leads in Territory</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {importSummary.totalInTerritory}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {importSummary.skipped > 0 && importSummary.errors?.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Button
                    size="small"
                    fullWidth
                    onClick={() => setShowSkippedDetails((prev) => !prev)}
                    endIcon={showSkippedDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{ justifyContent: "space-between", textTransform: "none" }}
                  >
                    {showSkippedDetails ? "Hide" : "Show"} skipped details
                  </Button>

                  <Collapse in={showSkippedDetails} timeout="auto" unmountOnExit>
                    <TableContainer
                      sx={{
                        maxHeight: 280,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        mt: 1,
                      }}
                    >
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Row</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Address</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {importSummary.errors.map((e, i) => (
                            <TableRow key={i}>
                              <TableCell>{e.row}</TableCell>
                              <TableCell sx={{ maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis" }}>
                                {e.address ?? "—"}
                              </TableCell>
                              <TableCell>{e.reason ?? ""}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<FileDownloadIcon />}
                        onClick={handleDownloadSkippedCSV}
                        sx={{ textTransform: "none", borderRadius: 2 }}
                      >
                        Download as CSV
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<FileDownloadIcon />}
                        onClick={handleDownloadSkippedTXT}
                        sx={{ textTransform: "none", borderRadius: 2 }}
                      >
                        Download as TXT
                      </Button>
                    </Stack>
                  </Collapse>
                </Box>
              )}

              <Box sx={{ mt: 2, textAlign: "right" }}>
                <Button
                  variant="outlined"
                  startIcon={<VisibilityIcon />}
                  onClick={handleViewLeads}
                  size="small"
                  sx={{ textTransform: "none", borderRadius: 2 }}
                >
                  View Leads
                </Button>
              </Box>
            </Box>
          )}

          {/* Existing individual-lead form, only visually wrapped */}
          {tab === 1 && (
            <Box sx={{ mt: 2 }}>
              <Box
                sx={{
                  mb: 2,
                  display: "flex",
                  alignItems: { xs: "flex-start", sm: "center" },
                  justifyContent: "space-between",
                  gap: 1,
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1rem" }}>
                    Add Individual Lead
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {territory?.name
                      ? `Manually add a single lead to ${territory.name}.`
                      : "Manually add a single lead to this territory."}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setTab(0)}
                  sx={{ textTransform: "none", borderRadius: 2 }}
                >
                  Back to Upload
                </Button>
              </Box>

            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <TextField
                  label="First Name"
                  name="first_name"
                  fullWidth
                  value={formData.first_name}
                  onChange={(e) =>
                    setFormData({ ...formData, first_name: e.target.value })
                  }
                />
                <TextField
                  label="Last Name"
                  name="last_name"
                  fullWidth
                  value={formData.last_name}
                  onChange={(e) =>
                    setFormData({ ...formData, last_name: e.target.value })
                  }
                />
              </Stack>

              <TextField
                label="Address"
                name="address"
                fullWidth
                value={formData.address || ""}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
              />

              <Stack direction="row" spacing={2}>
                <TextField
                  label="City"
                  name="city"
                  fullWidth
                  value={formData.city || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, city: e.target.value })
                  }
                />
                <StateSelect
                  label="State"
                  name="state"
                  value={formData.state || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, state: e.target.value })
                  }
                />
                <TextField
                  label="ZIP Code"
                  name="zip_code"
                  fullWidth
                  value={formData.zip_code || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, zip_code: e.target.value })
                  }
                />
              </Stack>

		<Stack direction="row" spacing={2}>
  <TextField
    label="Sold Date"
    type="date"
    value={formData.sold_date}
    onChange={(e) =>
      setFormData({ ...formData, sold_date: e.target.value })
    }
    InputLabelProps={{ shrink: true }}
    fullWidth
  />

  <TextField
    label="Property Type"
    value={formData.property_type}
    onChange={(e) =>
      setFormData({ ...formData, property_type: e.target.value })
    }
    fullWidth
  />
</Stack>

<Stack direction="row" spacing={2}>
  <TextField
    label="Price"
    type="number"
    value={formData.price}
    onChange={(e) =>
      setFormData({ ...formData, price: e.target.value })
    }
    fullWidth
  />

  <TextField
    label="Square Feet"
    type="number"
    value={formData.square_feet}
    onChange={(e) =>
      setFormData({ ...formData, square_feet: e.target.value })
    }
    fullWidth
  />

  <TextField
    label="Year Built"
    type="number"
    value={formData.year_built}
    onChange={(e) =>
      setFormData({ ...formData, year_built: e.target.value })
    }
    fullWidth
  />
</Stack>


              <Stack direction="row" spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    label="Status"
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.target.value })
                    }
                  >
                    {LeadStatusSelectMenuItems()}
                  </Select>
                </FormControl>
		{/* --------------Notes Field — moved here--------------- */}
<TextField
  label="Notes"
  name="notes"
  value={formData.notes || ""}
  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
  fullWidth
  multiline
  minRows={1}     // 🔽 reduced for consistent height
  maxRows={3}     // ✅ expands only when needed
  sx={{ mb: 2 }}
/>


              </Stack>

              <Stack direction="row" spacing={2}>
                <TextField
                  label="Latitude (REQUIRED)"
                  name="latitude"
                  fullWidth
                  required
                  value={formData.latitude}
                  onChange={(e) =>
                    setFormData({ ...formData, latitude: e.target.value })
                  }
                />
                <TextField
                  label="Longitude (REQUIRED)"
                  name="longitude"
                  fullWidth
                  required
                  value={formData.longitude}
                  onChange={(e) =>
                    setFormData({ ...formData, longitude: e.target.value })
                  }
                />
              </Stack>

              <Stack 
                direction={{ xs: "column", md: "row" }} 
                spacing={2} 
                justifyContent={{ xs: "stretch", md: "flex-end" }}
                sx={{ width: "100%" }}
              >
                <Button
                  variant="outlined"
                  onClick={() =>
                    setFormData({
                      first_name: "",
                      last_name: "",
                      address: "",
                      city: "",
                      state: "",
                      zip_code: "",
                      status: "New",
                      assigned_rep: "",
                      latitude: "",
                      longitude: "",
                      territory: territoryId || "",
                      notes: "",
                      sold_date: "",
                      property_type: "",
                      price: "",
                      square_feet: "",
                      year_built: "",
                    })
                  }
                  size="small"
                  sx={{ 
                    textTransform: "none", 
                    fontSize: { xs: "0.75rem", md: "0.875rem" },
                    width: { xs: "100%", md: "auto" }
                  }}
                >
                  Clear Form
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveLead}
                  size="small"
                  sx={{ 
                    fontSize: { xs: "0.75rem", md: "0.875rem" },
                    width: { xs: "100%", md: "auto" }
                  }}
                >
                  Save Lead
                </Button>
              </Stack>
            </Stack>

            </Box>
          )}
        </Paper>
        )}

        <ConfirmDialog
          open={confirmDeleteTerritory}
          onClose={() => setConfirmDeleteTerritory(false)}
          onConfirm={handleConfirmDeleteTerritory}
          title="Delete Territory"
          message="Are you sure you want to delete this territory? All associated leads will be unassigned. This action cannot be undone."
          itemName={territory?.name ? `Territory: ${territory.name}` : null}
        />
      </Box>
    </Box>
  );
}
